/**
 * KB-3418 — `campLocations` fields that only a server writer may set.
 *
 * `watchedSources` is stamped by the coverage rollup (`functions/src/camp-data/coverageRollup.ts`), and
 * the kb-ff rules refuse any client create that carries it or update that changes it. Pro's location
 * editor, however, round-trips WHOLE documents: the editor state is the live doc overlaid with the latest
 * draft snapshot, a save writes that payload back with `updateDoc`, and a publish writes a snapshot's
 * `data` wholesale. A snapshot taken before the rollup last changed the map would therefore carry a stale
 * value, and every later save or publish of that camp would be refused — a camp nobody can edit.
 *
 * KB-4297 adds the campHub fields, for the same round-trip reason with a quieter failure: no rule
 * refuses them, so a stale copy would simply be written back.
 * - `campHubKeys` / `campHubsEvaluatedAt` are maintained by `maintainCampHubAffiliations.ts`. The
 *   trigger does not re-evaluate on a `campHubKeys`-only write (that is what keeps it loop-safe), so a
 *   stale copy written by a save or publish would stand until something re-evaluates the camp.
 * - `campHubOverrides` is written only by the KB Admin tab (`campHubOverridesService.ts`), straight to
 *   the live doc. A draft snapshot from before that save would otherwise revert the override at publish.
 *
 * So these fields are removed at each point a client payload for `campLocations` (or a draft snapshot
 * that later becomes one) is built. Removing a key never changes the stored value: an absent key is not
 * part of an update's diff.
 */
export const SERVER_OWNED_CAMP_LOCATION_FIELDS = ["watchedSources", "campHubKeys", "campHubsEvaluatedAt"] as const;

/** Written by one dedicated client path, never by the editor's whole-document save or publish (KB-4297). */
export const DEDICATED_WRITER_CAMP_LOCATION_FIELDS = ["campHubOverrides"] as const;

/** Deletes the server-owned and dedicated-writer fields from `data` in place and returns it. */
export function omitServerOwnedCampLocationFields<T extends object>(data: T): T {
  const record = data as Record<string, unknown>;
  for (const field of SERVER_OWNED_CAMP_LOCATION_FIELDS) delete record[field];
  for (const field of DEDICATED_WRITER_CAMP_LOCATION_FIELDS) delete record[field];
  return data;
}
